import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { SettingsTab } from "@/components/admin/numeroloji/SettingsTab";
import { TemplatesTab } from "@/components/admin/numeroloji/TemplatesTab";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { MEMBERSHIP_FEATURES } from "../../shared/api";

import {
  Bell,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Download,
  Eye,
  FileDown,
  FileText,
  Filter,
  Forward,
  LayoutDashboard,
  ListOrdered,
  Mail,
  Moon,
  Package,
  PencilRuler,
  Search,
  ShoppingCart,
  Trash2,
} from "lucide-react";


type OrderStatus = "pending" | "paid" | "cancelled";

type Order = {
  id: string;
  createdAt: number;
  status: OrderStatus;
  paidAt?: number;
  totalTry: number;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  };
};

type Request = {
  id: string;
  createdAt: number;
  orderId: string;
  packageKey: string;
  reportLang: string;
  status: string;
  ad: string;
  soyad: string;
  email: string;
  gun?: number;
  ay?: number;
  yil?: number;
  mailStatus?: string;
  mailScheduledAt?: number;
  sentAt?: number;
  lastError?: string;
};

type CatalogPackage = {
  id: string;
  key: string;
  name: string;
  description: string;
  basePriceTry: number;
  active: boolean;
  requiresBirthDate: boolean;
  requiresPartner: boolean;
};

type CatalogProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  active: boolean;
  tags: string[];
};

type CatalogMeditation = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl?: string;
  audioUrl?: string;
  active: boolean;
  tags: string[];
};

type TemplateType =
  | "destiny"
  | "color"
  | "element"
  | "birth_day"
  | "expression"
  | "heart_desire"
  | "personality"
  | "karmic_dept"
  | "year_foresight"
  | "pin_code_1"
  | "pin_code_2"
  | "pin_code_3"
  | "pin_code_4"
  | "pin_code_5"
  | "pin_code_6"
  | "pin_code_7"
  | "pin_code_8"
  | "pin_code_9"
  | "pin_genel"
  | "iliski_genel"
  | "iliski_ruh_duygusu"
  | "iliski_element"
  | "iliski_isleme"
  | "email_subject"
  | "email_body";

type PinGenelMode = "yogunluk" | "dagilim" | "varsayilan";

type CatalogTemplate = {
  id: string;
  templateType: TemplateType;
  lang: "tr" | "en" | "es" | "de";
  title: string;
  content: string;
  calculatedId: string;
  pinGenelMode?: PinGenelMode;
  pinGenelDominant?: number;
  pinGenelMinCount?: number;
  pinGenelSecondary?: number;
  updatedAt: number;
};

type MembershipSettingsFeature = {
  key: string;
  label: string;
  includedInFree: boolean;
  includedInPlus: boolean;
  includedInPro: boolean;
};

type NumerolojiSettings = {
  payment: {
    ibanLabel: string;
    iban: string;
    accountName: string;
    note: string;
  };
  email: {
    fromName: string;
    fromEmail: string;
    bccAdmin?: boolean;
    retryMaxAttempts?: number;
  };
  branding?: {
    instagram?: string;
    phone?: string;
    website?: string;
  };
  ftp?: {
    host?: string;
    user?: string;
    password?: string;
    port?: number;
    secure?: boolean;
    baseDir?: string;
    publicBaseUrl?: string;
  };
  membership?: {
    features: MembershipSettingsFeature[];
  };
  delivery: {
    autoSendEnabled: boolean;
    minutes: {
      pin: number;
      isim: number;
      normal: number;
      premium: number;
      premiumRush: number;
      iliski: number;
    };
  };
};

type CatalogResponse = {
  ok: true;
  packages: CatalogPackage[];
  products: CatalogProduct[];
  meditations: CatalogMeditation[];
  templates: CatalogTemplate[];
  settings: NumerolojiSettings;
};

function formatDate(ts: number) {
  if (!ts) return "-";
  return new Date(ts * 1000).toLocaleString();
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ₺`;
  }
}

function MiniLineChart({ data }: { data: Array<{ label: string; total: number }> }) {
  const w = 640;
  const h = 220;
  const padX = 36;
  const padY = 20;

  const max = Math.max(1, ...data.map((d) => Number(d.total || 0)));
  const innerW = Math.max(1, w - padX * 2);
  const innerH = Math.max(1, h - padY * 2);
  const step = data.length > 1 ? innerW / (data.length - 1) : innerW;

  const points = data.map((d, i) => {
    const v = Number(d.total || 0);
    const x = padX + i * step;
    const y = padY + innerH - (v / max) * innerH;
    return { x, y, v, label: d.label };
  });

  const linePath = points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ");

  const areaPath =
    points.length > 0
      ? `M ${points[0].x.toFixed(2)} ${(padY + innerH).toFixed(2)} ${points
          .map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
          .join(" ")} L ${points[points.length - 1].x.toFixed(2)} ${(padY + innerH).toFixed(2)} Z`
      : "";

  const gridCount = 4;
  const gridYs = Array.from({ length: gridCount + 1 }, (_, i) => padY + (innerH / gridCount) * i);

  const last = points[points.length - 1];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="240" className="block">
      <defs>
        <linearGradient id="lineFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#3b82f6" stopOpacity="0.18" />
          <stop offset="1" stopColor="#3b82f6" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {gridYs.map((y) => (
        <line key={y} x1={padX} y1={y} x2={w - padX} y2={y} stroke="#e2e8f0" strokeWidth="1" />
      ))}

      {areaPath ? <path d={areaPath} fill="url(#lineFill)" /> : null}
      {linePath ? <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth="2.5" /> : null}

      {last ? <circle cx={last.x} cy={last.y} r={4} fill="#3b82f6" /> : null}

      {/* X axis labels */}
      {points.map((p) => (
        <text
          key={p.x}
          x={p.x}
          y={h - 6}
          textAnchor="middle"
          fontSize="11"
          fill="#64748b"
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
}

function MiniDonutChart({
  data,
  colors,
}: {
  data: Array<{ key: string; label: string; value: number }>;
  colors: Record<string, string>;
}) {
  const size = 220;
  const r = 78;
  const stroke = 22;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const total = Math.max(1, data.reduce((s, d) => s + Number(d.value || 0), 0));

  let offset = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="220" className="block">
      <g transform={`rotate(-90 ${c} ${c})`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />

        {data.map((d) => {
          const v = Number(d.value || 0);
          const dash = (v / total) * circumference;
          const segment = (
            <circle
              key={d.key}
              cx={c}
              cy={c}
              r={r}
              fill="none"
              stroke={colors[d.key] ?? "#3b82f6"}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return segment;
        })}
      </g>
    </svg>
  );
}

async function fetchOrders(): Promise<{ ok: true; items: Order[] }> {
  const res = await adminFetch("/api/admin/numeroloji/orders");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Siparişler alınamadı.");
  }
  return json as { ok: true; items: Order[] };
}

async function markPaid(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/paid`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İşlem başarısız.");
  }
  return json as any;
}

async function fetchRequests(): Promise<{ ok: true; items: Request[] }> {
  const res = await adminFetch("/api/admin/numeroloji/requests");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İstekler alınamadı.");
  }
  return json as { ok: true; items: Request[] };
}

async function deleteRequest(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/requests/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Silme başarısız.");
  }
  return json as any;
}

async function sendRequest(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/requests/${encodeURIComponent(id)}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Gönderme başarısız.");
  }
  return json as any;
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveSettings(payload: Partial<NumerolojiSettings>) {
  const res = await adminFetch("/api/admin/numeroloji/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ayarlar kaydedilemedi.");
  }
  return json as any;
}

async function fetchAdminPasswordStatus() {
  const res = await adminFetch("/api/admin/password/status");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şifre durumu alınamadı.");
  }
  return {
    hasPassword: Boolean(json?.hasPassword),
    managedIn:
      json?.managedIn === "database" || json?.managedIn === "env" || json?.managedIn === "none"
        ? json.managedIn
        : ("none" as const),
  };
}

async function updateAdminPassword(args: { currentPassword: string; newPassword: string; confirmPassword: string }) {
  const res = await adminFetch("/api/admin/password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şifre güncellenemedi.");
  }
  return json as { ok: true; managedIn: "database" };
}

async function saveTemplate(
  payload: Partial<CatalogTemplate> & {
    templateType: TemplateType;
    lang: string;
    content: string;
  },
) {
  const res = await adminFetch("/api/admin/numeroloji/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şablon kaydedilemedi.");
  }
  return json as any;
}

async function deleteTemplate(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/templates/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şablon silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

async function importTemplates(items: unknown[]) {
  const res = await adminFetch("/api/admin/numeroloji/templates/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şablonlar içe aktarılamadı.");
  }
  return json as { ok: true; importedCount: number };
}

async function savePackage(payload: Partial<CatalogPackage> & { key: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/packages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogPackage };
}

async function deletePackage(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/packages/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

async function saveProduct(payload: Partial<CatalogProduct> & { sku: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürün kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogProduct };
}

async function deleteProduct(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/products/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürün silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

async function saveMeditation(payload: Partial<CatalogMeditation> & { title: string }) {
  const res = await adminFetch("/api/admin/numeroloji/meditations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Meditasyon kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogMeditation };
}

async function deleteMeditation(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/meditations/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Meditasyon silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

function normalizeTab(value: string | null) {
  const v = (value ?? "").toLowerCase().trim();
  return v === "dashboard" ||
    v === "requests" ||
    v === "templates" ||
    v === "settings" ||
    v === "orders"
    ? v
    : "dashboard";
}

function normalizeSettingsSub(value: string | null) {
  const v = (value ?? "").toLowerCase().trim();
  return v === "payment" || v === "membership" || v === "security" ? v : "mail";
}

export default function AdminNumeroloji() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(() => normalizeTab(searchParams.get("tab")));
  const [settingsSub, setSettingsSub] = useState<"mail" | "payment" | "membership" | "security">(() =>
    normalizeSettingsSub(searchParams.get("sub")),
  );

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const rawTab = (searchParams.get("tab") ?? "").toLowerCase().trim();
  const shouldRedirectCatalog = rawTab === "catalog";

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();

  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const ordersQuery = useQuery({
    queryKey: ["admin-numeroloji-orders"],
    queryFn: fetchOrders,
    enabled: authenticated && !shouldRedirectCatalog,
  });

  const requestsQuery = useQuery({
    queryKey: ["admin-numeroloji-requests"],
    queryFn: fetchRequests,
    enabled: authenticated && !shouldRedirectCatalog,
  });

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated && !shouldRedirectCatalog,
    staleTime: 30_000,
  });

  const payMutation = useMutation({
    mutationFn: (id: string) => markPaid(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRequest(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: (id: string) => sendRequest(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request"] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const rid of ids) {
        await deleteRequest(rid);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (payload: Partial<NumerolojiSettings>) => saveSettings(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const passwordStatusQuery = useQuery({
    queryKey: ["admin-password-status"],
    queryFn: fetchAdminPasswordStatus,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const [passwordSuccess, setPasswordSuccess] = useState("");

  const passwordMutation = useMutation({
    mutationFn: (args: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
      updateAdminPassword(args),
    onSuccess: async () => {
      setPasswordSuccess("Yönetici şifresi güvenli şekilde güncellendi.");
      await queryClient.invalidateQueries({ queryKey: ["admin-password-status"] });
    },
  });

  const templateMutation = useMutation({
    mutationFn: (payload: any) => saveTemplate(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const templateDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const templateImportMutation = useMutation({
    mutationFn: (items: unknown[]) => importTemplates(items),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      alert(`${data.importedCount} şablon içe aktarıldı.`);
    },
  });

  const handleTemplateImportFile = async (file: File) => {
    const text = await file.text();

    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error("JSON dosyası okunamadı (geçerli JSON değil).");
    }

    const items = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.items) ? parsed.items : Array.isArray(parsed?.templates) ? parsed.templates : [];

    if (!items.length) {
      throw new Error("JSON içinde içe aktarılacak şablon bulunamadı.");
    }

    const ok = confirm(`${items.length} şablon "${templateLang}" dili için içe aktarılacak. Devam edilsin mi?`);
    if (!ok) return;

    const itemsWithLang = items.map((item: any) => ({ ...item, lang: templateLang }));
    templateImportMutation.mutate(itemsWithLang);
  };

  const packageSaveMutation = useMutation({
    mutationFn: (payload: any) => savePackage(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const packageDeleteMutation = useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const productSaveMutation = useMutation({
    mutationFn: (payload: any) => saveProduct(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const productDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const meditationSaveMutation = useMutation({
    mutationFn: (payload: any) => saveMeditation(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const meditationDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteMeditation(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const [meditationSearch, setMeditationSearch] = useState("");
  const [meditationFilter, setMeditationFilter] = useState<"all" | "active" | "inactive">("all");
  const [meditationSort, setMeditationSort] = useState<"default" | "title-asc" | "duration-asc" | "duration-desc">(
    "default",
  );

  const [packageSearch, setPackageSearch] = useState("");
  const [packageFilter, setPackageFilter] = useState<"all" | "active" | "inactive">("all");
  const [packageSort, setPackageSort] = useState<"default" | "price-asc" | "price-desc" | "name-asc" | "key-asc">(
    "default",
  );

  const [productSearch, setProductSearch] = useState("");
  const [productFilter, setProductFilter] = useState<"all" | "active" | "inactive">("all");
  const [productSort, setProductSort] = useState<"default" | "price-asc" | "price-desc" | "name-asc">("default");
  const [templateLang, setTemplateLang] = useState<"tr" | "en" | "es">("tr");

  const orders = useMemo(() => ordersQuery.data?.items ?? [], [ordersQuery.data]);
  const requests = useMemo(() => requestsQuery.data?.items ?? [], [requestsQuery.data]);

  const packages = useMemo(() => catalogQuery.data?.packages ?? [], [catalogQuery.data]);
  const products = useMemo(() => catalogQuery.data?.products ?? [], [catalogQuery.data]);
  const meditations = useMemo(() => catalogQuery.data?.meditations ?? [], [catalogQuery.data]);
  const allTemplates = useMemo(() => catalogQuery.data?.templates ?? [], [catalogQuery.data]);
  const templates = useMemo(
    () => allTemplates.filter((t) => t.lang === templateLang),
    [allTemplates, templateLang],
  );

  const visibleMeditations = useMemo(() => {
    const q = meditationSearch.trim().toLowerCase();

    let rows = meditations;

    if (q) {
      rows = rows.filter((m) => {
        const hay = `${m.title} ${m.description} ${m.level} ${(m.tags ?? []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (meditationFilter === "active") {
      rows = rows.filter((m) => m.active);
    } else if (meditationFilter === "inactive") {
      rows = rows.filter((m) => !m.active);
    }

    const sorted = [...rows];
    if (meditationSort === "title-asc") {
      sorted.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "tr"));
    } else if (meditationSort === "duration-asc") {
      sorted.sort((a, b) => Number(a.durationMinutes || 0) - Number(b.durationMinutes || 0));
    } else if (meditationSort === "duration-desc") {
      sorted.sort((a, b) => Number(b.durationMinutes || 0) - Number(a.durationMinutes || 0));
    }

    return sorted;
  }, [meditationFilter, meditationSearch, meditationSort, meditations]);

  const visibleProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();

    let rows = products;

    if (q) {
      rows = rows.filter((p) => {
        const hay = `${p.name} ${p.sku} ${p.category} ${(p.tags ?? []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (productFilter === "active") {
      rows = rows.filter((p) => p.active);
    } else if (productFilter === "inactive") {
      rows = rows.filter((p) => !p.active);
    }

    const sorted = [...rows];
    if (productSort === "price-asc") {
      sorted.sort((a, b) => Number(a.priceTry || 0) - Number(b.priceTry || 0));
    } else if (productSort === "price-desc") {
      sorted.sort((a, b) => Number(b.priceTry || 0) - Number(a.priceTry || 0));
    } else if (productSort === "name-asc") {
      sorted.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    }

    return sorted;
  }, [productFilter, productSearch, productSort, products]);

  const visiblePackages = useMemo(() => {
    const q = packageSearch.trim().toLowerCase();

    let rows = packages;

    if (q) {
      rows = rows.filter((p) => {
        const hay = `${p.name} ${p.key} ${p.description}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (packageFilter === "active") {
      rows = rows.filter((p) => p.active);
    } else if (packageFilter === "inactive") {
      rows = rows.filter((p) => !p.active);
    }

    const sorted = [...rows];
    if (packageSort === "price-asc") {
      sorted.sort((a, b) => Number(a.basePriceTry || 0) - Number(b.basePriceTry || 0));
    } else if (packageSort === "price-desc") {
      sorted.sort((a, b) => Number(b.basePriceTry || 0) - Number(a.basePriceTry || 0));
    } else if (packageSort === "name-asc") {
      sorted.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    } else if (packageSort === "key-asc") {
      sorted.sort((a, b) => String(a.key || "").localeCompare(String(b.key || "")));
    }

    return sorted;
  }, [packageFilter, packageSearch, packageSort, packages]);


  const paidOrders = useMemo(() => orders.filter((o) => o.status === "paid"), [orders]);
  const totalRevenue = useMemo(() => paidOrders.reduce((sum, o) => sum + Number(o.totalTry || 0), 0), [paidOrders]);
  const totalSales = paidOrders.length;

  const last30DaysRange = useMemo(() => {
    const end = Date.now();
    const start = end - 30 * 24 * 60 * 60 * 1000;
    const prevStart = start - 30 * 24 * 60 * 60 * 1000;
    return { prevStart, start, end };
  }, []);

  const revenueChangePct = useMemo(() => {
    const { prevStart, start, end } = last30DaysRange;
    const current = paidOrders
      .filter((o) => o.createdAt * 1000 >= start && o.createdAt * 1000 <= end)
      .reduce((s, o) => s + Number(o.totalTry || 0), 0);
    const prev = paidOrders
      .filter((o) => o.createdAt * 1000 >= prevStart && o.createdAt * 1000 < start)
      .reduce((s, o) => s + Number(o.totalTry || 0), 0);
    if (!prev) return 0;
    return ((current - prev) / prev) * 100;
  }, [paidOrders, last30DaysRange]);

  const salesChangePct = useMemo(() => {
    const { prevStart, start, end } = last30DaysRange;
    const current = paidOrders.filter((o) => o.createdAt * 1000 >= start && o.createdAt * 1000 <= end).length;
    const prev = paidOrders.filter((o) => o.createdAt * 1000 >= prevStart && o.createdAt * 1000 < start).length;
    if (!prev) return 0;
    return ((current - prev) / prev) * 100;
  }, [paidOrders, last30DaysRange]);

  const pendingRequests = useMemo(
    () => requests.filter((r) => r.mailStatus !== "sent" && r.status !== "sent").length,
    [requests],
  );

  const activeTemplatesCount = templates.length;

  useEffect(() => {
    if (!shouldRedirectCatalog) return;
    navigate("/admin/numeroloji/urun", { replace: true });
  }, [navigate, shouldRedirectCatalog]);

  useEffect(() => {
    const urlTab = normalizeTab(searchParams.get("tab"));
    const urlSub = normalizeSettingsSub(searchParams.get("sub"));

    if (urlTab !== tab) setTab(urlTab);
    if (urlSub !== settingsSub) setSettingsSub(urlSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);


  useEffect(() => {
    const hasParams = searchParams.has("tab") || searchParams.has("sub");

    if (!hasParams && tab === "dashboard" && settingsSub === "mail") {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (tab === "settings") {
      next.set("sub", settingsSub);
    } else {
      next.delete("sub");
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, settingsSub, tab]);

  const salesSeries = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const days: Array<{ key: string; label: string; startMs: number; endMs: number }> = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date(start);
      d.setDate(d.getDate() - i);
      const startMs = d.getTime();
      const endMs = startMs + 24 * 60 * 60 * 1000;
      const label = d
        .toLocaleDateString("tr-TR", { weekday: "short" })
        .replace(".", "")
        .replace(/\s/g, "");
      days.push({ key: d.toISOString().slice(0, 10), label, startMs, endMs });
    }

    return days.map((d) => {
      const total = paidOrders
        .filter((o) => o.createdAt * 1000 >= d.startMs && o.createdAt * 1000 < d.endMs)
        .reduce((s, o) => s + Number(o.totalTry || 0), 0);
      return { label: d.label, total };
    });
  }, [paidOrders]);

  const reportDistribution = useMemo(() => {
    const bucket = {
      full: 0,
      yearly: 0,
      destiny: 0,
      name: 0,
    };

    for (const r of requests) {
      if (r.packageKey === "premium") bucket.full += 1;
      else if (r.packageKey === "yil") bucket.yearly += 1;
      else if (r.packageKey === "isim") bucket.name += 1;
      else bucket.destiny += 1;
    }

    return [
      { key: "full", label: "Full Numeroloji", value: bucket.full },
      { key: "yearly", label: "Yıllık Öngörü", value: bucket.yearly },
      { key: "destiny", label: "Kader Sayısı", value: bucket.destiny },
      { key: "name", label: "İsim Analizi", value: bucket.name },
    ].filter((x) => x.value > 0);
  }, [requests]);

  const recentRequests = useMemo(() => {
    const sorted = [...requests].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
    return sorted.slice(0, 6);
  }, [requests]);

  const [requestSearch, setRequestSearch] = useState("");
  const [requestFilterPackage, setRequestFilterPackage] = useState<string>("");
  const [requestFilterMailStatus, setRequestFilterMailStatus] = useState<string>("");
  const [requestFilterDate, setRequestFilterDate] = useState("");
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsSelection, setRequestsSelection] = useState<Record<string, boolean>>({});

  const filteredRequests = useMemo(() => {
    const q = requestSearch.trim().toLowerCase();
    const pkg = requestFilterPackage.trim();
    const mail = requestFilterMailStatus.trim();

    return requests.filter((r) => {
      if (pkg && r.packageKey !== pkg) return false;
      if (mail && String(r.mailStatus ?? "").trim() !== mail) return false;

      if (q) {
        const hay = `${r.ad} ${r.soyad} ${r.email} ${r.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [requestFilterMailStatus, requestFilterPackage, requestSearch, requests]);

  const requestsPerPage = 8;
  const requestsTotalPages = Math.max(1, Math.ceil(filteredRequests.length / requestsPerPage));

  useEffect(() => {
    setRequestsPage((p) => Math.min(Math.max(1, p), requestsTotalPages));
  }, [requestsTotalPages]);

  const requestsPageItems = useMemo(() => {
    const start = (requestsPage - 1) * requestsPerPage;
    return filteredRequests.slice(start, start + requestsPerPage);
  }, [filteredRequests, requestsPage]);

  const requestsStats = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const startMs = startOfDay.getTime();
    const endMs = startMs + 24 * 60 * 60 * 1000;

    const today = requests.filter((r) => r.createdAt * 1000 >= startMs && r.createdAt * 1000 < endMs).length;
    const pending = requests.filter((r) => String(r.mailStatus ?? "").trim() === "pending").length;
    const sent = requests.filter((r) => String(r.mailStatus ?? "").trim() === "sent" || r.status === "sent").length;
    const failed = requests.filter((r) => String(r.mailStatus ?? "").trim() === "failed" || r.status === "failed").length;
    const considered = sent + failed;
    const successRate = considered ? (sent / considered) * 100 : 100;

    const bucket = new Map<string, number>();
    for (const r of requests) {
      bucket.set(r.packageKey, (bucket.get(r.packageKey) ?? 0) + 1);
    }

    const total = Math.max(1, requests.length);
    const getPct = (key: string) => ((bucket.get(key) ?? 0) / total) * 100;

    return {
      today,
      pending,
      successRate,
      dist: {
        premium: getPct("premium"),
        isim: getPct("isim"),
        pin: getPct("pin"),
      },
    };
  }, [requests]);

  const [settingsDraft, setSettingsDraft] = useState<NumerolojiSettings | null>(null);
  useEffect(() => {
    const raw = catalogQuery.data?.settings;
    if (!raw) return;

    setSettingsDraft({
      payment: raw.payment,
      email: {
        fromName: raw.email.fromName,
        fromEmail: raw.email.fromEmail,
        bccAdmin: raw.email.bccAdmin ?? false,
        retryMaxAttempts: raw.email.retryMaxAttempts ?? 5,
      },
      ftp: {
        host: String(raw.ftp?.host ?? ""),
        user: String(raw.ftp?.user ?? ""),
        password: String(raw.ftp?.password ?? ""),
        port: Number(raw.ftp?.port ?? 21),
        secure: Boolean(raw.ftp?.secure),
        baseDir: String(raw.ftp?.baseDir ?? "public_html/meditasyon"),
        publicBaseUrl: String(raw.ftp?.publicBaseUrl ?? "https://33n.org/meditasyon"),
      },
      delivery: raw.delivery ?? {
        autoSendEnabled: false,
        minutes: {
          pin: 12 * 60,
          isim: 24 * 60,
          normal: 48 * 60,
          premium: 36 * 60,
          premiumRush: 12 * 60,
          iliski: 48 * 60,
        },
      },
      membership: {
        features: (() => {
          const incoming = Array.isArray(raw.membership?.features) ? raw.membership.features : [];
          const incomingByKey = new Map<string, (typeof incoming)[number]>();
          for (const feature of incoming) {
            const key = String(feature?.key ?? "").trim();
            if (!key) continue;
            incomingByKey.set(key, feature);
          }

          return MEMBERSHIP_FEATURES.map((feature) => {
            const incomingFeature = incomingByKey.get(feature.key);
            if (!incomingFeature) return { ...feature };

            return {
              ...feature,
              label: String(incomingFeature.label ?? "").trim() || feature.label,
              includedInFree: Boolean(incomingFeature.includedInFree),
              includedInPlus: Boolean(incomingFeature.includedInPlus),
              includedInPro: Boolean(incomingFeature.includedInPro),
            };
          });
        })(),
      },
    });
  }, [catalogQuery.data?.settings]);

  const [templateMode, setTemplateMode] = useState<"list" | "edit">("list");
  const [templateDraft, setTemplateDraft] = useState<CatalogTemplate | null>(null);
  const [templateEditorView, setTemplateEditorView] = useState<"code" | "preview">("code");

  const [dashboardSelection, setDashboardSelection] = useState<Record<string, boolean>>({});

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (sessionQuery.isError) {
    return <div className="text-sm text-destructive">{(sessionQuery.error as Error).message}</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const pageTitle =
    tab === "dashboard"
      ? "Admin Genel Bakış"
      : tab === "requests"
        ? "Gelişmiş İstek Yönetimi"
        : tab === "templates"
          ? "Şablon Listesi"
          : tab === "settings"
              ? settingsSub === "mail"
                ? "Mail Ayarları"
                : settingsSub === "payment"
                  ? "Ödeme Ayarları"
                  : settingsSub === "membership"
                    ? "Üyelik Planları"
                    : "Güvenlik"
              : tab === "orders"
                ? "Sipariş Listesi"
                : "Yönetim";

  const selectedIds = Object.entries(dashboardSelection)
    .filter(([, v]) => Boolean(v))
    .map(([id]) => id);

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <div className="flex min-h-dvh w-full">
        <NumerolojiAdminSidebar
          mode="tabs"
          tab={tab as any}
          setTab={setTab as any}
          settingsSub={settingsSub}
          setSettingsSub={setSettingsSub}
          isDark={isDark}
          onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
        />

        <div className="flex min-w-0 flex-1 flex-col bg-white dark:bg-slate-950">
          <header className="h-16 border-b border-slate-200/70 bg-white px-4 dark:border-slate-800/60 dark:bg-slate-950 sm:px-6">
            <div className="flex h-full items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <NumerolojiAdminMobileSidebar
                  mode="tabs"
                  tab={tab as any}
                  setTab={setTab as any}
                  settingsSub={settingsSub}
                  setSettingsSub={setSettingsSub}
                  isDark={isDark}
                  onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
                />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                  {pageTitle}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {tab === "requests" ? (
                  <div className="relative hidden sm:block">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      className="w-64 rounded-full bg-slate-100 py-1.5 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                      placeholder="İsteklerde ara..."
                      value={requestSearch}
                      onChange={(e) => setRequestSearch(e.target.value)}
                    />
                  </div>
                ) : null}

                <button
                  type="button"
                  className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                  aria-label="Bildirimler"
                >
                  <Bell className="h-4 w-4" />
                </button>

                <div className="hidden items-center gap-3 border-l border-slate-200 pl-4 dark:border-slate-800 sm:flex">
                  <div className="text-right leading-tight">
                    <div className="text-sm font-medium">Admin Kullanıcı</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Yönetici</div>
                  </div>
                  <div className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-orange-100 text-sm font-semibold text-orange-700 dark:border-slate-800 dark:bg-orange-500/15 dark:text-orange-300">
                    A
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="rounded-full"
                  size="sm"
                  onClick={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </header>

          {sessionQuery.data?.numerolojiStorage === "memory" ? (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100 sm:px-6">
              Uyarı: Veri tabanı bağlantısı sunucuda aktif değil. Oluşturulan istekler kalıcı kaydedilmez ve sunucu
              yenilenince kaybolabilir. (Sunucuda SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY gerekli.)
            </div>
          ) : null}

          <div className="min-w-0 flex-1 p-4 sm:p-6">
            <div className="min-w-0">
              <TabsContent value="dashboard" className="mt-0">
                <div className="grid gap-6">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Toplam Kazanç</div>
                          <div className="mt-1 text-2xl font-semibold tracking-tight">{formatTry(totalRevenue)}</div>
                          <div className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                            {revenueChangePct >= 0 ? "+" : ""}
                            {revenueChangePct.toFixed(1)}% Geçen Ay
                          </div>
                        </div>
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                          <CircleDollarSign className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Toplam Satış</div>
                          <div className="mt-1 text-2xl font-semibold tracking-tight">{totalSales.toLocaleString("tr-TR")}</div>
                          <div className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                            {salesChangePct >= 0 ? "+" : ""}
                            {salesChangePct.toFixed(1)}% Geçen Ay
                          </div>
                        </div>
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                          <ShoppingCart className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Bekleyen İstekler</div>
                          <div className="mt-1 text-2xl font-semibold tracking-tight">{pendingRequests.toLocaleString("tr-TR")}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Hemen ilgilen</div>
                        </div>
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                          <ListOrdered className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Aktif Şablonlar</div>
                          <div className="mt-1 text-2xl font-semibold tracking-tight">{activeTemplatesCount.toLocaleString("tr-TR")}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Güncel veri</div>
                        </div>
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                          <FileText className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Satış Trendleri</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Son 7 Gün</div>
                      </div>
                      <div className="mt-3">
                        <div className="h-[240px] w-full">
                          <MiniLineChart data={salesSeries} />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="text-sm font-semibold">Rapor Dağılımı</div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-[240px_1fr] sm:items-center">
                        <MiniDonutChart
                          data={reportDistribution}
                          colors={{
                            full: "#3b82f6",
                            yearly: "#8b5cf6",
                            destiny: "#f59e0b",
                            name: "#10b981",
                          }}
                        />

                        <div className="grid gap-2 text-xs">
                          {reportDistribution.map((d) => {
                            const color =
                              d.key === "full"
                                ? "#3b82f6"
                                : d.key === "yearly"
                                  ? "#8b5cf6"
                                  : d.key === "destiny"
                                    ? "#f59e0b"
                                    : "#10b981";

                            return (
                              <div key={d.key} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                                <span className="flex-1">{d.label}</span>
                                <span className="font-medium text-slate-900 dark:text-slate-50">
                                  {d.value.toLocaleString("tr-TR")}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm font-semibold">Son İstekler</div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          disabled={!selectedIds.length || bulkDeleteMutation.isPending}
                          onClick={() => {
                            const ok = confirm(`${selectedIds.length} istek silinecek. Devam edilsin mi?`);
                            if (!ok) return;
                            bulkDeleteMutation.mutate(selectedIds, {
                              onSuccess: () => setDashboardSelection({}),
                            });
                          }}
                        >
                          Seçilenleri Sil
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-full"
                          disabled={!requests.length || bulkDeleteMutation.isPending}
                          onClick={() => {
                            const ok = confirm(`Tüm istekler (${requests.length}) silinecek. Devam edilsin mi?`);
                            if (!ok) return;
                            bulkDeleteMutation.mutate(
                              requests.map((r) => r.id),
                              { onSuccess: () => setDashboardSelection({}) },
                            );
                          }}
                        >
                          Tümünü Sil
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 overflow-x-auto">
                      <div className="min-w-[820px]">
                        <div className="grid grid-cols-[36px_1.2fr_1fr_0.7fr_0.6fr_0.7fr] gap-2 border-b pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          <div />
                          <div>Ad Soyad</div>
                          <div>Analiz</div>
                          <div>Gönderilme Tarihi</div>
                          <div>Durum</div>
                          <div>İşlem</div>
                        </div>

                        <div className="divide-y">
                          {recentRequests.map((r) => {
                            const selected = Boolean(dashboardSelection[r.id]);
                            const statusLabel =
                              r.mailStatus === "sent" || r.status === "sent"
                                ? "Gönderildi"
                                : r.mailStatus === "pending"
                                  ? "Beklemede"
                                  : r.mailStatus === "failed" || r.status === "failed"
                                    ? "Hata"
                                    : "Ortay";

                            const statusClass =
                              statusLabel === "Gönderildi"
                                ? "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                : statusLabel === "Beklemede"
                                  ? "bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                                  : statusLabel === "Hata"
                                    ? "bg-red-500/10 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                                    : "bg-slate-500/10 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300";

                            const analysisLabel =
                              r.packageKey === "premium"
                                ? "Premium Numeroloji (Full)"
                                : r.packageKey === "yil"
                                  ? "Yıllık Öngörü"
                                  : r.packageKey === "isim"
                                    ? "İsim Analizi"
                                    : r.packageKey === "pin"
                                      ? "PIN Analizi"
                                      : r.packageKey === "iliski"
                                        ? "İlişki Analizi"
                                        : "Kader Sayısı Analizi";

                            return (
                              <div key={r.id} className="grid grid-cols-[36px_1.2fr_1fr_0.7fr_0.6fr_0.7fr] items-center gap-2 py-3 text-sm">
                                <div>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) =>
                                      setDashboardSelection((prev) => ({ ...prev, [r.id]: e.target.checked }))
                                    }
                                  />
                                </div>
                                <div className="font-medium">
                                  <button
                                    type="button"
                                    className="hover:underline"
                                    onClick={() => navigate(`/admin/numeroloji/${r.id}`)}
                                  >
                                    {r.ad} {r.soyad}
                                  </button>
                                </div>
                                <div className="text-slate-600 dark:text-slate-300">{analysisLabel}</div>
                                <div className="text-slate-600 dark:text-slate-300">{formatDate(r.sentAt)}</div>
                                <div>
                                  <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-semibold", statusClass)}>
                                    {statusLabel}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 rounded-full"
                                    onClick={() => navigate(`/admin/numeroloji/${r.id}`)}
                                  >
                                    Detay
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="h-8 rounded-full"
                                    disabled={deleteMutation.isPending}
                                    onClick={() => {
                                      const ok = confirm("Bu isteği silmek istiyor musunuz?");
                                      if (!ok) return;
                                      deleteMutation.mutate(r.id);
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="orders" className="mt-0">
          <Card className="overflow-x-auto">
            <div className="min-w-[980px] p-4">
              {ordersQuery.isLoading ? (
                <div className="text-sm text-muted-foreground">Yükleniyor...</div>
              ) : ordersQuery.isError ? (
                <div className="text-sm text-destructive">{(ordersQuery.error as Error).message}</div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground">Toplam: {orders.length}</div>

                  <div className="mt-3 grid grid-cols-[160px_1.2fr_0.8fr_0.8fr_1fr] gap-2 border-b pb-2 text-xs font-semibold text-muted-foreground">
                    <div>Sipariş</div>
                    <div>Müşteri</div>
                    <div>Tutar</div>
                    <div>Durum</div>
                    <div className="text-right">İşlem</div>
                  </div>

                  <div className="divide-y">
                    {orders.map((o) => (
                      <div
                        key={o.id}
                        className="grid grid-cols-[160px_1.2fr_0.8fr_0.8fr_1fr] gap-2 py-3 text-sm"
                      >
                        <div className="text-muted-foreground">
                          <div className="font-medium text-foreground">#{o.id}</div>
                          <div className="text-xs text-muted-foreground">{formatDate(o.createdAt)}</div>
                        </div>
                        <div>
                          <div className="font-medium">
                            {o.customer.firstName} {o.customer.lastName}
                          </div>
                          <div className="text-xs text-muted-foreground">{o.customer.email}</div>
                        </div>
                        <div className="text-muted-foreground">{formatTry(o.totalTry)}</div>
                        <div className="text-muted-foreground">
                          {o.status === "paid" ? "Ödendi" : o.status === "pending" ? "Bekliyor" : "İptal"}
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            className="rounded-full"
                            disabled={o.status === "paid" || payMutation.isPending}
                            onClick={() => {
                              const ok = confirm("Bu siparişi 'ödendi' olarak onaylamak istiyor musunuz?");
                              if (!ok) return;
                              payMutation.mutate(o.id);
                            }}
                          >
                            Ödemeyi onayla
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="mt-0">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-blue-700">
                  <Link to="/admin/numeroloji/yeni" className="inline-flex items-center gap-2">
                    <span className="text-sm">+</span>
                    Manuel İstek Oluştur
                  </Link>
                </Button>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                >
                  <Filter className="h-4 w-4" />
                  Toplu İşlemler
                </button>

                <Button
                  variant="outline"
                  className="rounded-lg"
                  size="sm"
                  disabled={!Object.values(requestsSelection).some(Boolean) || bulkDeleteMutation.isPending}
                  onClick={() => {
                    const ids = Object.entries(requestsSelection)
                      .filter(([, v]) => Boolean(v))
                      .map(([id]) => id);
                    if (!ids.length) return;
                    const ok = confirm(`${ids.length} istek silinecek. Devam edilsin mi?`);
                    if (!ok) return;
                    bulkDeleteMutation.mutate(ids, { onSuccess: () => setRequestsSelection({}) });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Seçilenleri Sil
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                >
                  <Download className="h-4 w-4" />
                  Dışa Aktar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40 md:grid-cols-4">
              <div className="flex flex-col">
                <label className="mb-1.5 ml-1 text-xs font-bold uppercase text-slate-500">Tarih Aralığı</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-50"
                    placeholder="01.02.2026 - 28.02.2026"
                    value={requestFilterDate}
                    onChange={(e) => setRequestFilterDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="mb-1.5 ml-1 text-xs font-bold uppercase text-slate-500">Analiz Tipi</label>
                <select
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 px-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-50"
                  value={requestFilterPackage}
                  onChange={(e) => {
                    setRequestFilterPackage(e.target.value);
                    setRequestsPage(1);
                  }}
                >
                  <option value="">Tümü</option>
                  <option value="premium">Premium Full</option>
                  <option value="normal">Numeroloji Analizi</option>
                  <option value="isim">İsim Analizi</option>
                  <option value="pin">Pin Kodu</option>
                  <option value="iliski">İlişki Uyumu</option>
                </select>
              </div>

              <div className="flex flex-col">
                <label className="mb-1.5 ml-1 text-xs font-bold uppercase text-slate-500">Mail Durumu</label>
                <select
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 px-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-50"
                  value={requestFilterMailStatus}
                  onChange={(e) => {
                    setRequestFilterMailStatus(e.target.value);
                    setRequestsPage(1);
                  }}
                >
                  <option value="">Tümü</option>
                  <option value="sent">Gönderildi</option>
                  <option value="pending">Sırada</option>
                  <option value="failed">Hata</option>
                  <option value="manual">Manuel</option>
                  <option value="processing">İşleniyor</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-800 py-2 text-sm font-medium text-white hover:bg-slate-700 dark:bg-slate-700"
                  onClick={() => setRequestsPage(1)}
                >
                  <Filter className="h-4 w-4" />
                  Filtrele
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/30">
                    <tr>
                      <th className="w-12 px-6 py-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600 dark:border-slate-700 dark:bg-slate-900"
                          checked={
                            requestsPageItems.length > 0 &&
                            requestsPageItems.every((r) => Boolean(requestsSelection[r.id]))
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setRequestsSelection((prev) => {
                              const next = { ...prev };
                              for (const r of requestsPageItems) next[r.id] = checked;
                              return next;
                            });
                          }}
                        />
                      </th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Müşteri / E-posta</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Analiz Tipi</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">İşlem Tarihi</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Mail Durumu</th>
                      <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">İşlemler</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {requestsQuery.isLoading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">
                          Yükleniyor...
                        </td>
                      </tr>
                    ) : requestsQuery.isError ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-red-600">
                          {(requestsQuery.error as Error).message}
                        </td>
                      </tr>
                    ) : requestsPageItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">
                          Sonuç bulunamadı.
                        </td>
                      </tr>
                    ) : (
                      requestsPageItems.map((r) => {
                        const selected = Boolean(requestsSelection[r.id]);

                        const mail = String(r.mailStatus ?? "").trim();
                        const statusDot =
                          mail === "sent" ? "bg-emerald-500" : mail === "pending" ? "bg-amber-500" : mail === "failed" ? "bg-red-500" : "bg-slate-400";
                        const statusText =
                          mail === "sent"
                            ? "Gönderildi"
                            : mail === "pending"
                              ? "Sırada"
                              : mail === "failed"
                                ? "Hata"
                                : mail === "manual"
                                  ? "Manuel"
                                  : mail === "processing"
                                    ? "İşleniyor"
                                    : "-";
                        const statusTextClass =
                          mail === "sent"
                            ? "text-emerald-600"
                            : mail === "pending"
                              ? "text-amber-600"
                              : mail === "failed"
                                ? "text-red-600"
                                : "text-slate-500";

                        const analysis =
                          r.packageKey === "premium"
                            ? { label: "PREMIUM FULL", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" }
                            : r.packageKey === "isim"
                              ? { label: "İSİM ANALİZİ", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" }
                              : r.packageKey === "pin"
                                ? { label: "PİN KODU", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400" }
                                : r.packageKey === "iliski"
                                  ? { label: "İLİŞKİ", cls: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400" }
                                  : { label: "NORMAL", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" };

                        const date = formatDate(r.createdAt);

                        return (
                          <tr key={r.id} className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/30">
                            <td className="px-6 py-4">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600 dark:border-slate-700 dark:bg-slate-900"
                                checked={selected}
                                onChange={(e) => setRequestsSelection((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-semibold text-slate-800 dark:text-slate-100">
                                {r.ad} {r.soyad}
                              </div>
                              <div className="text-xs text-slate-500">{r.email}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-bold", analysis.cls)}>
                                {analysis.label}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-slate-600 dark:text-slate-400">{date}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className={cn("h-2 w-2 rounded-full", statusDot)} />
                                <span className={cn("text-sm font-medium", statusTextClass)}>{statusText}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg text-slate-400 hover:bg-blue-600/5 hover:text-blue-600"
                                  title="Görüntüle"
                                  onClick={() => navigate(`/admin/numeroloji/${r.id}`)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg text-slate-400 hover:bg-amber-500/5 hover:text-amber-500"
                                  title="Tekrar Gönder"
                                  disabled={sendMutation.isPending}
                                  onClick={() => sendMutation.mutate(r.id)}
                                >
                                  <Forward className="h-4 w-4" />
                                </Button>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg text-slate-400 hover:bg-emerald-500/5 hover:text-emerald-500"
                                  title="PDF İndir"
                                  onClick={() => navigate(`/admin/numeroloji/${r.id}`)}
                                >
                                  <FileDown className="h-4 w-4" />
                                </Button>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg text-slate-400 hover:bg-red-500/5 hover:text-red-500"
                                  title="Sil"
                                  disabled={deleteMutation.isPending}
                                  onClick={() => {
                                    const ok = confirm("Bu isteği silmek istiyor musunuz?");
                                    if (!ok) return;
                                    deleteMutation.mutate(r.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 text-sm text-slate-500 dark:border-slate-800">
                <div>
                  Toplam {filteredRequests.length} sonuçtan {(requestsPage - 1) * requestsPerPage + 1}-
                  {Math.min(filteredRequests.length, requestsPage * requestsPerPage)} gösteriliyor
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-slate-200 p-2 text-slate-400 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/30"
                    onClick={() => setRequestsPage((p) => Math.max(1, p - 1))}
                    disabled={requestsPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from({ length: Math.min(3, requestsTotalPages) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={cn(
                        "h-8 w-8 rounded-md text-sm font-medium",
                        p === requestsPage
                          ? "bg-blue-600 text-white"
                          : "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900/30",
                      )}
                      onClick={() => setRequestsPage(p)}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="rounded-md border border-slate-200 p-2 text-slate-400 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/30"
                    onClick={() => setRequestsPage((p) => Math.min(requestsTotalPages, p + 1))}
                    disabled={requestsPage >= requestsTotalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 grid gap-6 xl:grid-cols-3">
              <div className="grid gap-4 sm:grid-cols-3 xl:col-span-2">
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Bugünkü İstekler</span>
                    <span className="rounded-lg bg-blue-600/10 p-1.5 text-blue-600 dark:text-blue-400">
                      <Search className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="text-2xl font-bold">{requestsStats.today}</div>
                  <div className="mt-1 text-[11px] font-medium text-slate-400">Gün içi toplam</div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Bekleyenler</span>
                    <span className="rounded-lg bg-amber-500/10 p-1.5 text-amber-600 dark:text-amber-400">
                      <Filter className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="text-2xl font-bold">{requestsStats.pending}</div>
                  <div className="mt-1 text-[11px] font-medium text-slate-400">Kuyrukta bekliyor</div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Başarı Oranı</span>
                    <span className="rounded-lg bg-emerald-500/10 p-1.5 text-emerald-600 dark:text-emerald-400">
                      <Download className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="text-2xl font-bold">{requestsStats.successRate.toFixed(1)}%</div>
                  <div className="mt-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">Sorunsuz teslimat</div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-white">
                  <span className="text-blue-600 dark:text-blue-400">•</span>
                  Analiz Dağılımı
                </h3>

                <div className="space-y-4">
                  {(
                    [
                      { key: "premium", label: "Premium Full", color: "bg-blue-500", pct: requestsStats.dist.premium },
                      { key: "isim", label: "İsim Analizi", color: "bg-purple-500", pct: requestsStats.dist.isim },
                      { key: "pin", label: "Pin Kodu", color: "bg-indigo-500", pct: requestsStats.dist.pin },
                    ] as const
                  ).map((row) => (
                    <div key={row.key}>
                      <div className="mb-1.5 flex justify-between text-xs">
                        <span className="font-medium text-slate-600 dark:text-slate-400">{row.label}</span>
                        <span className="font-bold text-slate-800 dark:text-slate-200">{row.pct.toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div className={cn("h-full rounded-full", row.color)} style={{ width: `${Math.min(100, row.pct)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="catalog" className="mt-0">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <div className="grid gap-6">
              <div
                id="meditasyon-yonetimi"
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40"
              >
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Meditasyon Yönetimi</div>
                    <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                      Etiketleyerek kişiye özel meditasyonlar sunabilirsiniz.
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                        <div className="text-xl font-extrabold">{meditations.filter((m) => m.active).length}</div>
                      </div>
                    </div>

                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                        <div className="h-2 w-2 rounded-full bg-slate-400" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pasif</div>
                        <div className="text-xl font-extrabold">{meditations.filter((m) => !m.active).length}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                      placeholder="Başlık, seviye, etiket..."
                      value={meditationSearch}
                      onChange={(e) => setMeditationSearch(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant={meditationFilter === "all" ? "default" : "outline"}
                      className={cn("rounded-full", meditationFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setMeditationFilter("all")}
                    >
                      Tümü
                    </Button>
                    <Button
                      size="sm"
                      variant={meditationFilter === "active" ? "default" : "outline"}
                      className={cn("rounded-full", meditationFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setMeditationFilter("active")}
                    >
                      Aktif
                    </Button>
                    <Button
                      size="sm"
                      variant={meditationFilter === "inactive" ? "default" : "outline"}
                      className={cn("rounded-full", meditationFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setMeditationFilter("inactive")}
                    >
                      Pasif
                    </Button>

                    <div className="ml-0 flex items-center gap-2 lg:ml-2">
                      <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                      <select
                        className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                        value={meditationSort}
                        onChange={(e) => setMeditationSort(e.target.value as any)}
                      >
                        <option value="default">Varsayılan</option>
                        <option value="title-asc">Başlık (A-Z)</option>
                        <option value="duration-asc">Süre (Artan)</option>
                        <option value="duration-desc">Süre (Azalan)</option>
                      </select>
                    </div>

                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate("/admin/numeroloji/meditasyon/yeni")}
                    >
                      <span className="mr-1">+</span>
                      Yeni meditasyon
                    </Button>
                  </div>
                </div>

                {meditationSaveMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(meditationSaveMutation.error as Error).message}
                  </div>
                ) : null}

                {meditationDeleteMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(meditationDeleteMutation.error as Error).message}
                  </div>
                ) : null}

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visibleMeditations.map((m) => {
                    const badge = m.active ? "Aktif" : "Pasif";
                    const badgeClass = m.active ? "bg-emerald-500" : "bg-slate-500";

                    return (
                      <div
                        key={m.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className="relative aspect-[4/3] bg-gradient-to-br from-indigo-600/25 via-blue-600/10 to-slate-950/20">
                          <div
                            className={cn(
                              "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                              badgeClass,
                            )}
                          >
                            {badge}
                          </div>

                          <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() => navigate(`/admin/numeroloji/meditasyon/${encodeURIComponent(m.id)}`)}
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={meditationDeleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu meditasyonu silmek istiyor musunuz?");
                                if (!ok) return;
                                meditationDeleteMutation.mutate(m.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                            {m.durationMinutes} dk • {m.level}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="text-base font-bold text-slate-900 dark:text-white">{m.title}</div>
                          <div className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">{m.description}</div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-1">
                              {(m.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span
                                  key={`${m.id}-tag-${idx}`}
                                  className="rounded-full bg-blue-600/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={m.active}
                                disabled={meditationSaveMutation.isPending}
                                onChange={() =>
                                  meditationSaveMutation.mutate({
                                    ...m,
                                    active: !m.active,
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate("/admin/numeroloji/meditasyon/yeni")}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">Yeni meditasyon ekle</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      Yeni bir meditasyon seansı oluştur.
                    </div>
                  </button>
                </div>
              </div>

              <div
                id="urun-yonetimi"
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40"
              >
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Ürün Yönetimi</div>
                    <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                      Dijital raporlarınızı ve satışa açık ürünlerinizi buradan yönetebilirsiniz.
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Aktif
                        </div>
                        <div className="text-xl font-extrabold">
                          {products.filter((p) => p.active).length}
                        </div>
                      </div>
                    </div>

                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                        <div className="h-2 w-2 rounded-full bg-slate-400" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Pasif
                        </div>
                        <div className="text-xl font-extrabold">
                          {products.filter((p) => !p.active).length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                      placeholder="Ürün adı, SKU, kategori, etiket..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant={productFilter === "all" ? "default" : "outline"}
                      className={cn("rounded-full", productFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setProductFilter("all")}
                    >
                      Tümü
                    </Button>
                    <Button
                      size="sm"
                      variant={productFilter === "active" ? "default" : "outline"}
                      className={cn("rounded-full", productFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setProductFilter("active")}
                    >
                      Aktif
                    </Button>
                    <Button
                      size="sm"
                      variant={productFilter === "inactive" ? "default" : "outline"}
                      className={cn("rounded-full", productFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setProductFilter("inactive")}
                    >
                      Pasif
                    </Button>

                    <div className="ml-0 flex items-center gap-2 lg:ml-2">
                      <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                      <select
                        className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                        value={productSort}
                        onChange={(e) => setProductSort(e.target.value as any)}
                      >
                        <option value="default">Varsayılan</option>
                        <option value="name-asc">Ad (A-Z)</option>
                        <option value="price-asc">Fiyat (Artan)</option>
                        <option value="price-desc">Fiyat (Azalan)</option>
                      </select>
                    </div>

                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate("/admin/numeroloji/urun/yeni")}
                    >
                      <span className="mr-1">+</span>
                      Yeni ürün
                    </Button>
                  </div>
                </div>

                {productSaveMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(productSaveMutation.error as Error).message}
                  </div>
                ) : null}

                {productDeleteMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(productDeleteMutation.error as Error).message}
                  </div>
                ) : null}

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visibleProducts.map((p) => {
                    const badge = p.active ? "Aktif" : "Pasif";
                    const badgeClass = p.active ? "bg-emerald-500" : "bg-slate-500";
                    const heroClass =
                      p.category === "bundle"
                        ? "from-blue-600/30 via-indigo-600/20 to-slate-950/20"
                        : p.category === "oil"
                          ? "from-amber-500/25 via-orange-500/15 to-slate-950/20"
                          : p.category === "stone"
                            ? "from-emerald-500/20 via-teal-500/15 to-slate-950/20"
                            : "from-slate-500/20 via-slate-400/10 to-slate-950/20";

                    return (
                      <div
                        key={p.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className={cn("relative aspect-[4/3] bg-gradient-to-br", heroClass)}>
                          <div className={cn("absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white", badgeClass)}>
                            {badge}
                          </div>

                          <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() => navigate(`/admin/numeroloji/urun/${encodeURIComponent(p.id)}`)}
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={productDeleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu ürünü silmek istiyor musunuz?");
                                if (!ok) return;
                                productDeleteMutation.mutate(p.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                            {p.category}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="text-base font-bold text-slate-900 dark:text-white">{p.name}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">SKU: {p.sku}</div>
                          <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">{p.description}</div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="text-lg font-extrabold text-blue-600 dark:text-blue-300">{formatTry(p.priceTry)}</div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={p.active}
                                disabled={productSaveMutation.isPending}
                                onChange={() =>
                                  productSaveMutation.mutate({
                                    id: p.id,
                                    sku: p.sku,
                                    name: p.name,
                                    description: p.description,
                                    category: p.category,
                                    priceTry: p.priceTry,
                                    active: !p.active,
                                    tags: p.tags ?? [],
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate("/admin/numeroloji/urun/yeni")}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">Yeni ürün ekle</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      Yeni bir rapor veya hizmet paketi oluştur.
                    </div>
                  </button>
                </div>
              </div>


              <Card className="p-6">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">Numeroloji Paketleri</div>
                    <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                      Analiz paketleri, fiyatlar ve zorunlu alan ayarları.
                      <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                        Düzenlemek için paketin sağ üstündeki kaleme tıklayın; form aşağıda açılır.
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Aktif
                        </div>
                        <div className="text-xl font-extrabold">{packages.filter((p) => p.active).length}</div>
                      </div>
                    </div>

                    <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                        <div className="h-2 w-2 rounded-full bg-slate-400" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Pasif
                        </div>
                        <div className="text-xl font-extrabold">{packages.filter((p) => !p.active).length}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                      placeholder="Paket adı, key veya açıklama..."
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant={packageFilter === "all" ? "default" : "outline"}
                      className={cn("rounded-full", packageFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setPackageFilter("all")}
                    >
                      Tümü
                    </Button>
                    <Button
                      size="sm"
                      variant={packageFilter === "active" ? "default" : "outline"}
                      className={cn("rounded-full", packageFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setPackageFilter("active")}
                    >
                      Aktif
                    </Button>
                    <Button
                      size="sm"
                      variant={packageFilter === "inactive" ? "default" : "outline"}
                      className={cn("rounded-full", packageFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                      onClick={() => setPackageFilter("inactive")}
                    >
                      Pasif
                    </Button>

                    <div className="ml-0 flex items-center gap-2 lg:ml-2">
                      <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                      <select
                        className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                        value={packageSort}
                        onChange={(e) => setPackageSort(e.target.value as any)}
                      >
                        <option value="default">Varsayılan</option>
                        <option value="key-asc">Key (A-Z)</option>
                        <option value="name-asc">Ad (A-Z)</option>
                        <option value="price-asc">Fiyat (Artan)</option>
                        <option value="price-desc">Fiyat (Azalan)</option>
                      </select>
                    </div>

                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate("/admin/numeroloji/paket/yeni")}
                    >
                      <span className="mr-1">+</span>
                      Yeni paket
                    </Button>
                  </div>
                </div>

                {packageSaveMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(packageSaveMutation.error as Error).message}
                  </div>
                ) : null}

                {packageDeleteMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {(packageDeleteMutation.error as Error).message}
                  </div>
                ) : null}

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visiblePackages.map((p) => {
                    const badge = p.active ? "Aktif" : "Pasif";
                    const badgeClass = p.active ? "bg-emerald-500" : "bg-slate-500";
                    const heroClass =
                      p.key === "premium"
                        ? "from-blue-600/30 via-indigo-600/20 to-slate-950/20"
                        : p.key === "pin"
                          ? "from-indigo-600/30 via-violet-600/20 to-slate-950/20"
                          : p.key === "isim"
                            ? "from-purple-600/30 via-fuchsia-600/15 to-slate-950/20"
                            : p.key === "iliski"
                              ? "from-rose-600/25 via-orange-600/10 to-slate-950/20"
                              : "from-slate-500/20 via-slate-400/10 to-slate-950/20";

                    return (
                      <div
                        key={p.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className={cn("relative aspect-[4/3] bg-gradient-to-br", heroClass)}>
                          <div className={cn("absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white", badgeClass)}>
                            {badge}
                          </div>

                          <div className="absolute right-3 top-3 flex gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() => navigate(`/admin/numeroloji/paket/${encodeURIComponent(p.id)}`)}
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={packageDeleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu paketi silmek istiyor musunuz?");
                                if (!ok) return;
                                packageDeleteMutation.mutate(p.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                            key: {p.key}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="text-base font-bold text-slate-900 dark:text-white">{p.name}</div>
                          <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">{p.description}</div>

                          <div className="mt-4 flex flex-wrap gap-2 text-xs">
                            <span className="rounded-full bg-slate-100 px-2 py-1 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              Doğum tarihi: {p.requiresBirthDate ? "Gerekli" : "Yok"}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-1 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              Partner: {p.requiresPartner ? "Gerekli" : "Yok"}
                            </span>
                          </div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="text-lg font-extrabold text-blue-600 dark:text-blue-300">{formatTry(p.basePriceTry)}</div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={p.active}
                                disabled={packageSaveMutation.isPending}
                                onChange={() =>
                                  packageSaveMutation.mutate({
                                    id: p.id,
                                    key: p.key,
                                    name: p.name,
                                    description: p.description,
                                    basePriceTry: p.basePriceTry,
                                    active: !p.active,
                                    requiresBirthDate: p.requiresBirthDate,
                                    requiresPartner: p.requiresPartner,
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate("/admin/numeroloji/paket/yeni")}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">Yeni paket ekle</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      Yeni bir analiz paketi oluştur.
                    </div>
                  </button>
                </div>
              </Card>

            </div>
          )}
        </TabsContent>

        <TabsContent value="settings">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : !settingsDraft ? (
            <div className="text-sm text-muted-foreground">Ayarlar yüklenemedi.</div>
          ) : (
            <SettingsTab
              settings={settingsDraft}
              settingsSub={settingsSub}
              onChange={(updater) => setSettingsDraft((prev) => (prev ? updater(prev) : prev))}
              onSave={() => settingsMutation.mutate(settingsDraft)}
              isSaving={settingsMutation.isPending}
              error={settingsMutation.isError ? (settingsMutation.error as Error).message : undefined}
              passwordStatus={passwordStatusQuery.data}
              onChangeAdminPassword={(args) => {
                setPasswordSuccess("");
                passwordMutation.mutate(args);
              }}
              isChangingPassword={passwordMutation.isPending}
              passwordError={passwordMutation.isError ? (passwordMutation.error as Error).message : undefined}
              passwordSuccess={passwordSuccess}
            />
          )}
        </TabsContent>

        <TabsContent value="templates">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <TemplatesTab
              templates={templates}
              selectedLang={templateLang}
              onLangChange={(lang) => {
                setTemplateLang(lang);
                setTemplateMode("list");
                setTemplateDraft(null);
              }}
              mode={templateMode}
              draft={templateDraft}
              editorView={templateEditorView}
              isSaving={templateMutation.isPending}
              isDeleting={templateDeleteMutation.isPending}
              isImporting={templateImportMutation.isPending}
              error={[
                templateMutation.isError ? (templateMutation.error as Error).message : "",
                templateDeleteMutation.isError ? (templateDeleteMutation.error as Error).message : "",
                templateImportMutation.isError ? (templateImportMutation.error as Error).message : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onModeChange={(nextMode) => {
                setTemplateMode(nextMode);
                if (nextMode === "list") {
                  setTemplateDraft(null);
                }
              }}
              onDraftChange={(updater) => setTemplateDraft((prev) => (prev ? updater(prev) : prev))}
              onEditorViewChange={setTemplateEditorView}
              onCreate={() => {
                setTemplateMode("edit");
                setTemplateEditorView("code");
                setTemplateDraft({
                  id: "",
                  templateType: "destiny",
                  lang: templateLang,
                  title: "",
                  content: "",
                  calculatedId: "",
                  updatedAt: Math.floor(Date.now() / 1000),
                });
              }}
              onEdit={(template) => {
                setTemplateDraft({ ...template });
                setTemplateMode("edit");
                setTemplateEditorView("code");
              }}
              onSave={() => {
                if (!templateDraft) return;
                templateMutation.mutate({
                  id: templateDraft.id || undefined,
                  templateType: templateDraft.templateType,
                  lang: templateDraft.lang,
                  title: templateDraft.title,
                  content: templateDraft.content,
                  calculatedId: templateDraft.calculatedId,
                  pinGenelMode: templateDraft.pinGenelMode,
                  pinGenelDominant: templateDraft.pinGenelDominant,
                  pinGenelMinCount: templateDraft.pinGenelMinCount,
                  pinGenelSecondary: templateDraft.pinGenelSecondary,
                });
              }}
              onDelete={(id) => templateDeleteMutation.mutate(id)}
              onImportFile={handleTemplateImportFile}
            />
          )}
        </TabsContent>
            </div>
          </div>
        </div>
      </div>
    </Tabs>
  );
}
